<template>
  <div>
    <v-container fluid>
      <!-- Page header -->
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar :image="SubNavbarBackGround" color="primary" class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">
            {{ isEditMode ? (isViewOnly ? 'View Voice Config' : 'Edit Voice Config') : 'Add Voice Config' }}
          </v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-form ref="form" v-model="valid">
        <!-- General information -->
        <v-card class="config-card mt-4" elevation="2">
          <v-card-title class="card-header">
            <v-icon color="white" class="mr-3">mdi-account-voice</v-icon>
            <span>Voice Configuration</span>
          </v-card-title>
          <v-card-text class="card-content">
            <v-row>
              <v-col cols="12" md="5">
                <v-text-field
                  id="field-name-voiceconfigdetail"
                  label="Name"
                  v-model="form.name"
                  :rules="[rules.required]"
                  :disabled="isViewOnly"
                  density="comfortable"
                  variant="outlined"
                >
                  <template v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-tag-outline</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="5">
                <!-- Display ID is generated by the server, never sent back in the payload -->
                <v-text-field
                  id="field-displayid-voiceconfigdetail"
                  label="Display ID"
                  v-model="form.displayId"
                  class="generated-field"
                  readonly
                  persistent-hint
                  hint="Generated automatically"
                  :disabled="!isEditMode"
                  :placeholder="isEditMode ? '' : 'Generated on save'"
                  :persistent-placeholder="!isEditMode"
                  density="comfortable"
                  variant="outlined"
                >
                  <template v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-identifier</v-icon>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Voice services -->
        <v-card class="config-card mt-4" elevation="2">
          <v-card-title class="card-header">
            <v-icon color="white" class="mr-3">mdi-translate</v-icon>
            <span>Voice Services</span>
          </v-card-title>
          <v-card-text class="card-content">
            <div class="service-tabs">
              <v-tabs v-model="activeTab" color="primary" slider-color="primary" show-arrows>
                <v-tab v-for="(tab, tabIndex) in tabDefs" :key="'tab-' + tabIndex" :value="tabIndex">
                  {{ tab.label }}
                </v-tab>
              </v-tabs>

              <v-window v-model="activeTab" :transition="false" :reverse-transition="false">
                <v-window-item v-for="(tab, tabIndex) in tabDefs" :key="'window-' + tabIndex" :value="tabIndex">
                  <!-- ============ Speech to Text (single, not addable) ============ -->
                  <div v-if="tab.type === 'stt'" class="mt-4">
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          label="Speech to Text Endpoint"
                          v-model="form.content.stt.endpoint"
                          :disabled="isViewOnly"
                          density="comfortable"
                          variant="outlined"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon size="small" color="grey">mdi-link</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          label="Speech to Text Key"
                          v-model="form.content.stt.apiKey"
                          :type="form.content.stt.showValue ? 'text' : 'password'"
                          :disabled="isViewOnly"
                          density="comfortable"
                          variant="outlined"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon size="small" color="grey">mdi-key</v-icon>
                          </template>
                          <template v-slot:append-inner>
                            <v-btn icon size="small" variant="text" density="comfortable" @click="form.content.stt.showValue = !form.content.stt.showValue">
                              <v-icon size="small">{{ form.content.stt.showValue ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                            </v-btn>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          label="Speech to Text Region"
                          v-model="form.content.stt.region"
                          :disabled="isViewOnly"
                          density="comfortable"
                          variant="outlined"
                        >
                          <template v-slot:prepend-inner>
                            <v-icon size="small" color="grey">mdi-map-marker-outline</v-icon>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-switch v-model="form.content.stt.useEndpointIOS" inset color="primary" hide-details :disabled="isViewOnly" label="Use Endpoint for iOS" />
                      </v-col>
                      <v-col cols="12" md="3">
                        <v-switch v-model="form.content.stt.useEndpointAndroid" inset color="primary" hide-details :disabled="isViewOnly" label="Use Endpoint for Android" />
                      </v-col>
                    </v-row>
                  </div>

                  <!-- ============ Text to Speech (addable) ============ -->
                  <div v-else-if="tab.type === 'tts'" class="mt-4">
                    <div v-for="(item, index) in form.content.tts" :key="'tts-' + index" class="endpoint-card">
                      <v-row no-gutters>
                        <v-col cols="12" md="4" class="px-2">
                          <v-text-field label="Text to Speech Endpoint" v-model="item.endpoint" :disabled="isViewOnly" density="comfortable" variant="outlined">
                            <template v-slot:prepend-inner>
                              <v-icon size="small" color="grey">mdi-link</v-icon>
                            </template>
                          </v-text-field>
                        </v-col>
                        <v-col cols="12" md="3" class="px-2">
                          <v-text-field
                            label="API Key"
                            v-model="item.apiKey"
                            :type="item.showValue ? 'text' : 'password'"
                            :disabled="isViewOnly"
                            density="comfortable"
                            variant="outlined"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon size="small" color="grey">mdi-key</v-icon>
                            </template>
                            <template v-slot:append-inner>
                              <v-btn icon size="small" variant="text" density="comfortable" @click="item.showValue = !item.showValue">
                                <v-icon size="small">{{ item.showValue ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                              </v-btn>
                            </template>
                          </v-text-field>
                        </v-col>
                        <v-col cols="12" md="4" class="px-2">
                          <v-text-field label="Region" v-model="item.region" :disabled="isViewOnly" density="comfortable" variant="outlined">
                            <template v-slot:prepend-inner>
                              <v-icon size="small" color="grey">mdi-map-marker-outline</v-icon>
                            </template>
                          </v-text-field>
                        </v-col>
                        <v-col cols="12" md="1" class="d-flex align-center justify-end">
                          <v-btn v-if="!isViewOnly" icon color="error" variant="text" density="comfortable" @click="DeleteTts(index)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </div>

                    <div v-if="!form.content.tts.length" class="empty-hint">No Text to Speech endpoint configured.</div>

                    <v-btn v-if="!isViewOnly" outlined color="primary" class="add-endpoint-btn" @click="AddTts()">
                      <v-icon location="left">mdi-plus</v-icon>
                      Add Text to Speech
                    </v-btn>
                  </div>

                  <!-- ============ Gemini Text to Speech (addable) ============ -->
                  <div v-else class="mt-4">
                    <div v-for="(item, index) in form.content.geminiTts" :key="'gemini-' + index" class="endpoint-card">
                      <v-row no-gutters>
                        <v-col cols="12" md="5" class="px-2">
                          <v-text-field label="Endpoint" v-model="item.endpoint" :disabled="isViewOnly" density="comfortable" variant="outlined">
                            <template v-slot:prepend-inner>
                              <v-icon size="small" color="grey">mdi-link</v-icon>
                            </template>
                          </v-text-field>
                        </v-col>
                        <v-col cols="12" md="3" class="px-2">
                          <v-text-field
                            label="API Key"
                            v-model="item.apiKey"
                            :type="item.showValue ? 'text' : 'password'"
                            :disabled="isViewOnly"
                            density="comfortable"
                            variant="outlined"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon size="small" color="grey">mdi-key</v-icon>
                            </template>
                            <template v-slot:append-inner>
                              <v-btn icon size="small" variant="text" density="comfortable" @click="item.showValue = !item.showValue">
                                <v-icon size="small">{{ item.showValue ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                              </v-btn>
                            </template>
                          </v-text-field>
                        </v-col>
                        <v-col cols="12" md="3" class="px-2">
                          <v-text-field label="Region" v-model="item.region" :disabled="isViewOnly" density="comfortable" variant="outlined">
                            <template v-slot:prepend-inner>
                              <v-icon size="small" color="grey">mdi-map-marker-outline</v-icon>
                            </template>
                          </v-text-field>
                        </v-col>
                        <v-col cols="12" md="1" class="d-flex align-center justify-end">
                          <v-btn v-if="!isViewOnly" icon color="error" variant="text" density="comfortable" @click="DeleteGeminiTts(index)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>

                      <v-row no-gutters class="mt-2">
                        <v-col cols="12" md="3" class="px-2">
                          <v-combobox
                            label="Model"
                            v-model="item.model"
                            :items="geminiTtsModelOptions"
                            :disabled="isViewOnly"
                            density="comfortable"
                            variant="outlined"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon size="small" color="grey">mdi-cube-outline</v-icon>
                            </template>
                          </v-combobox>
                        </v-col>
                        <v-col cols="12" md="3" class="px-2">
                          <v-combobox
                            label="Voice Name EN"
                            v-model="item.voiceNameEn"
                            :items="geminiVoiceOptions"
                            :disabled="isViewOnly"
                            density="comfortable"
                            variant="outlined"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon size="small" color="grey">mdi-account-voice</v-icon>
                            </template>
                          </v-combobox>
                        </v-col>
                        <v-col cols="12" md="3" class="px-2">
                          <v-combobox
                            label="Voice Name TH"
                            v-model="item.voiceNameTh"
                            :items="geminiVoiceOptions"
                            :disabled="isViewOnly"
                            density="comfortable"
                            variant="outlined"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon size="small" color="grey">mdi-account-voice</v-icon>
                            </template>
                          </v-combobox>
                        </v-col>
                        <v-col cols="12" md="3" class="px-2">
                          <v-combobox
                            label="Voice Name CN"
                            v-model="item.voiceNameCn"
                            :items="geminiVoiceOptions"
                            :disabled="isViewOnly"
                            density="comfortable"
                            variant="outlined"
                          >
                            <template v-slot:prepend-inner>
                              <v-icon size="small" color="grey">mdi-account-voice</v-icon>
                            </template>
                          </v-combobox>
                        </v-col>
                      </v-row>

                      <v-row no-gutters class="mt-2">
                        <v-col cols="12" class="px-2">
                          <v-textarea
                            label="Instruction"
                            v-model="item.instruction"
                            :disabled="isViewOnly"
                            density="comfortable"
                            variant="outlined"
                            rows="2"
                            auto-grow
                            placeholder="e.g. Say the following in a curious way."
                          >
                            <template v-slot:prepend-inner>
                              <v-icon size="small" color="grey">mdi-tune-variant</v-icon>
                            </template>
                          </v-textarea>
                        </v-col>
                      </v-row>
                    </div>

                    <div v-if="!form.content.geminiTts.length" class="empty-hint">No Gemini Text to Speech endpoint configured.</div>

                    <v-btn v-if="!isViewOnly" outlined color="primary" class="add-endpoint-btn" @click="AddGeminiTts()">
                      <v-icon location="left">mdi-plus</v-icon>
                      Add Gemini Text to Speech
                    </v-btn>
                  </div>
                </v-window-item>
              </v-window>
            </div>
          </v-card-text>
        </v-card>

        <!-- Actions -->
        <v-row class="mt-6 mb-8">
          <v-col cols="12" class="d-flex justify-end">
            <v-btn id="button-cancel-voiceconfigdetail" variant="outlined" color="warning" size="large" class="mr-4" @click="Back()">
              <v-icon start>mdi-close</v-icon>
              CANCEL
            </v-btn>
            <v-btn v-if="!isViewOnly" id="button-save-voiceconfigdetail" variant="elevated" color="primary" size="large" @click="Save()">
              <v-icon start>mdi-content-save</v-icon>
              SAVE
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-container>

    <!-- loading -->
    <v-overlay :model-value="dialog" class="align-center justify-center" persistent>
      <v-card class="loading-card" elevation="8">
        <v-card-text class="text-center">
          <v-progress-circular indeterminate size="64" color="primary" class="mb-4"></v-progress-circular>
          <h3 class="loading-title">Loading...</h3>
          <p class="loading-subtitle">Please wait</p>
        </v-card-text>
      </v-card>
    </v-overlay>
  </div>
</template>

<script>
import checkPermissionTwo from '@/api/function.js';

const GEMINI_DEFAULT_MODEL = 'gemini-2.5-flash-preview-tts';

export default {
  name: 'VoiceConfigDetail',
  inject: ['flagNav', 'FontColorTitle', 'SubNavbarBackGround'],
  mixins: [checkPermissionTwo],
  components: {},
  data: () => ({
    today: null,
    dialog: false,
    valid: true,
    activeTab: 0,
    rules: {
      required: (v) => !!v || 'Required.',
    },
    form: {
      id: null,
      name: null,
      displayId: null,
      content: {
        stt: {
          id: 0,
          endpoint: null,
          apiKey: null,
          region: null,
          useEndpointIOS: false,
          useEndpointAndroid: false,
          showValue: false,
        },
        tts: [],
        geminiTts: [],
      },
    },
    geminiTtsModelOptions: ['gemini-2.5-flash-preview-tts', 'gemini-2.5-pro-preview-tts', 'gemini-3.1-flash-tts-preview'],
    geminiVoiceOptions: [
      'Achernar',
      'Achird',
      'Algenib',
      'Algieba',
      'Alnilam',
      'Aoede',
      'Autonoe',
      'Callirrhoe',
      'Charon',
      'Despina',
      'Enceladus',
      'Erinome',
      'Fenrir',
      'Gacrux',
      'Iapetus',
      'Kore',
      'Laomedeia',
      'Leda',
      'Orus',
      'Pulcherrima',
      'Puck',
      'Rasalgethi',
      'Sadachbia',
      'Sadaltager',
      'Schedar',
      'Sulafat',
      'Umbriel',
      'Vindemiatrix',
      'Zephyr',
      'Zubenelgenubi',
    ],
  }),
  computed: {
    isEditMode() {
      return !!this.$route.query.id;
    },
    isViewOnly() {
      return !(this.$pagePermission && this.$pagePermission.canEdit('VoiceConfigs'));
    },
    // A voice config is one reusable bundle of settings — three service tabs, no modes.
    tabDefs() {
      return [
        { type: 'stt', label: 'Speech to Text' },
        { type: 'tts', label: 'Text to Speech' },
        { type: 'gemini', label: 'Gemini Text to Speech' },
      ];
    },
  },
  methods: {
    Get(id) {
      let self = this;
      self.dialog = true;
      self.$call.GetVoiceConfigurationById(
        id,
        (dataResponse) => {
          self.dialog = false;
          if (dataResponse.status == 0 && dataResponse.data) {
            self.Bind(dataResponse.data);
          } else {
            self.$swal.fire({
              icon: 'error',
              title: 'Error!',
              text: dataResponse.message || 'Voice configuration not found',
              confirmButtonColor: '#d33',
            });
          }
        },
        (errorResource) => {
          self.dialog = false;
          console.error(errorResource);
          self.$swal.fire({
            icon: 'error',
            title: 'Error!',
            text: 'Failed to load voice configuration',
            confirmButtonColor: '#d33',
          });
        }
      );
    },
    Bind(data) {
      let self = this;
      self.form.id = data.id;
      self.form.name = data.name;
      self.form.displayId = data.displayId;
      self.form.content = self.normalizeContent(data);
      self.mergeLoadedOptions();
    },
    normalizeContent(source) {
      let data = source || {};
      return {
        stt: this.normalizeStt(data.stt),
        tts: (data.tts || []).map((e) => this.normalizeTts(e)),
        geminiTts: (data.geminiTts || []).map((e) => this.normalizeGemini(e)),
      };
    },
    // `stt` can come back null when it was never filled in — normalise it to an empty object.
    normalizeStt(stt) {
      let source = stt || {};
      return {
        id: source.id || 0,
        endpoint: source.endpoint || null,
        apiKey: source.apiKey || null,
        region: source.region || null,
        useEndpointIOS: source.useEndpointIOS === true,
        useEndpointAndroid: source.useEndpointAndroid === true,
        showValue: false,
      };
    },
    normalizeTts(item) {
      let source = item || {};
      return {
        id: source.id || 0,
        endpoint: source.endpoint || null,
        apiKey: source.apiKey || null,
        region: source.region || null,
        showValue: false,
      };
    },
    normalizeGemini(item) {
      let source = item || {};
      return {
        id: source.id || 0,
        endpoint: source.endpoint || null,
        apiKey: source.apiKey || null,
        region: source.region || null,
        model: source.model || null,
        instruction: source.instruction || null,
        voiceNameEn: source.voiceNameEn || null,
        voiceNameTh: source.voiceNameTh || null,
        voiceNameCn: source.voiceNameCn || null,
        showValue: false,
      };
    },
    // Keep values that came from the API selectable even when they are not in the built-in lists.
    mergeLoadedOptions() {
      let self = this;
      self.form.content.geminiTts.forEach((item) => {
        if (item.model && !self.geminiTtsModelOptions.includes(item.model)) {
          self.geminiTtsModelOptions.push(item.model);
        }
        [item.voiceNameEn, item.voiceNameTh, item.voiceNameCn].forEach((voice) => {
          if (voice && !self.geminiVoiceOptions.includes(voice)) {
            self.geminiVoiceOptions.push(voice);
          }
        });
      });
    },
    AddTts() {
      this.form.content.tts.push({
        id: 0,
        endpoint: null,
        apiKey: null,
        region: null,
        showValue: false,
      });
    },
    DeleteTts(index) {
      let self = this;
      self.$swal
        .fire({
          title: self.$t('confirmDeletion'),
          text: 'Delete this Text to Speech configuration?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: self.$t('confirm'),
          cancelButtonText: self.$t('cancel'),
          reverseButtons: false,
        })
        .then((result) => {
          if (result.isConfirmed) {
            self.form.content.tts.splice(index, 1);
          }
        });
    },
    AddGeminiTts() {
      this.form.content.geminiTts.push({
        id: 0,
        endpoint: `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_DEFAULT_MODEL}:generateContent`,
        apiKey: null,
        region: null,
        model: GEMINI_DEFAULT_MODEL,
        instruction: null,
        voiceNameEn: null,
        voiceNameTh: null,
        voiceNameCn: null,
        showValue: false,
      });
    },
    DeleteGeminiTts(index) {
      let self = this;
      self.$swal
        .fire({
          title: self.$t('confirmDeletion'),
          text: 'Delete this Gemini Text to Speech configuration?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: self.$t('confirm'),
          cancelButtonText: self.$t('cancel'),
          reverseButtons: false,
        })
        .then((result) => {
          if (result.isConfirmed) {
            self.form.content.geminiTts.splice(index, 1);
          }
        });
    },
    isBlank(value) {
      return value === null || value === undefined || String(value).trim() === '';
    },
    buildStt(stt) {
      return {
        id: stt.id || 0,
        endpoint: stt.endpoint || '',
        apiKey: stt.apiKey || '',
        region: stt.region || '',
        useEndpointIOS: stt.useEndpointIOS === true,
        useEndpointAndroid: stt.useEndpointAndroid === true,
      };
    },
    buildContent() {
      let self = this;
      let source = self.form.content;
      return {
        stt: self.buildStt(source.stt),
        // drop rows the user added but never filled in
        tts: source.tts
          .filter((e) => !(self.isBlank(e.endpoint) && self.isBlank(e.apiKey) && self.isBlank(e.region)))
          .map((e) => ({
            id: e.id || 0,
            endpoint: e.endpoint || '',
            apiKey: e.apiKey || '',
            region: e.region || '',
          })),
        geminiTts: source.geminiTts
          .filter((e) => !(self.isBlank(e.apiKey) && self.isBlank(e.region) && self.isBlank(e.voiceNameEn) && self.isBlank(e.voiceNameTh) && self.isBlank(e.voiceNameCn)))
          .map((e) => ({
            id: e.id || 0,
            endpoint: e.endpoint || '',
            apiKey: e.apiKey || '',
            region: e.region || '',
            model: e.model || '',
            instruction: e.instruction || '',
            voiceNameEn: e.voiceNameEn || '',
            voiceNameTh: e.voiceNameTh || '',
            voiceNameCn: e.voiceNameCn || '',
          })),
      };
    },
    FindRowMissingEndpoint(content) {
      let self = this;
      if (content.tts.some((e) => self.isBlank(e.endpoint))) {
        return 'Text to Speech';
      }
      if (content.geminiTts.some((e) => self.isBlank(e.endpoint))) {
        return 'Gemini Text to Speech';
      }
      return null;
    },
    async Save() {
      let self = this;
      const { valid } = await self.$refs.form.validate();
      if (!valid) {
        self.$swal.fire({
          icon: 'warning',
          title: 'Incomplete data',
          text: 'Please fill in all required fields.',
          confirmButtonColor: '#3085d6',
        });
        return;
      }

      let content = self.buildContent();

      // แถวที่ไม่มี Endpoint จะถูก BAL ตัดทิ้งเงียบ ๆ
      // เตือนตรงนี้แทน ไม่งั้นผู้ใช้เห็น toast ว่าบันทึกแล้วแต่แถวหายไปตอนเปิดกลับมา
      let missingEndpoint = self.FindRowMissingEndpoint(content);
      if (missingEndpoint) {
        self.$swal.fire({
          icon: 'warning',
          title: 'Endpoint required',
          text: `Please fill in the Endpoint for every ${missingEndpoint} row, or remove the row.`,
          confirmButtonColor: '#3085d6',
        });
        return;
      }

      // displayId is generated by the server — never send it back.
      let temp = {
        name: self.form.name,
        stt: content.stt,
        tts: content.tts,
        geminiTts: content.geminiTts,
      };

      self.dialog = true;
      if (self.isEditMode) {
        temp.id = Number(self.form.id || self.$route.query.id);
        self.$call.UpdateVoiceConfiguration(temp, self.SaveSuccess, self.SaveError);
      } else {
        self.$call.CreateVoiceConfiguration(temp, self.SaveSuccess, self.SaveError);
      }
    },
    SaveSuccess(dataResponse) {
      let self = this;
      self.dialog = false;
      if (dataResponse.status == 0) {
        self.$swal.fire({
          icon: 'success',
          title: dataResponse.message || 'Saved successfully',
          showConfirmButton: false,
          timer: 1500,
        });
        self.$router.push({ name: 'VoiceConfigs' });
      } else {
        self.$swal.fire({
          icon: 'error',
          title: 'Error!',
          text: dataResponse.message || 'Failed to save voice configuration',
          confirmButtonColor: '#d33',
        });
      }
    },
    SaveError(errorResource) {
      let self = this;
      self.dialog = false;
      console.error(errorResource);
      self.$swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to save voice configuration',
        confirmButtonColor: '#d33',
      });
    },
    Back() {
      this.$router.push({ name: 'VoiceConfigs' });
    },
    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1;
      let yyyy = today1.getFullYear();
      if (dd < 10) {
        dd = '0' + dd;
      }
      if (mm < 10) {
        mm = '0' + mm;
      }
      this.today = dd + '/' + mm + '/' + yyyy;
    },
  },
  mounted() {
    let self = this;
    self.GetDate();
    let id = self.$route.query.id;
    if (id) {
      self.Get(id);
    } else {
      // Add mode — start with one empty row per addable list.
      self.AddTts();
      self.AddGeminiTts();
    }
  },
};
</script>

<style scoped>
.config-card {
  border-radius: 16px !important;
  border: none;
  background: white;
  transition: all 0.3s ease;
  width: 100%;
}

.config-card:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

.card-header {
  background: linear-gradient(135deg, #0e81df 30%, #e2e8f0 100%);
  border-radius: 16px 16px 0 0 !important;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
  color: white;
}

.card-header .v-card__title {
  font-size: 1.3rem;
  font-weight: 500;
  color: #495057;
}

.card-content {
  padding: 24px;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.section-title::before {
  content: '';
  width: 4px;
  height: 20px;
  background: #667eea;
  margin-right: 12px;
  border-radius: 2px;
}

.endpoint-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #e9ecef;
}

.add-endpoint-btn {
  color: #1867c0 !important;
  border: 2px dashed #1867c0 !important;
  background: transparent !important;
  border-radius: 12px;
  height: 56px;
  width: 100%;
  margin-top: 16px;
  transition: all 0.3s ease;
}

.add-endpoint-btn:hover {
  border-color: #667eea !important;
  background: rgba(102, 126, 234, 0.05) !important;
}

.service-tabs .v-tabs {
  border-radius: 12px;
  background: #f8f9fa;
}

/* Server-generated value — muted so it does not read as an editable field */
.generated-field :deep(.v-field) {
  background: #f5f5f5;
}

.generated-field :deep(input) {
  color: rgba(0, 0, 0, 0.6);
}

.empty-hint {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  padding: 12px 4px;
}

.loading-card {
  border-radius: 16px;
  padding: 8px 16px;
}

.loading-title {
  font-size: 1.1rem;
  font-weight: 600;
}

.loading-subtitle {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
}
</style>
